/**
 * Defers below-fold autoplay video: poster first, preload=none,
 * attach <source> when near the viewport.
 */
class DeferredAutoplayVideo extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    if (window.theme && typeof theme.initSectionVisible === 'function') {
      theme.initSectionVisible({
        element: this,
        callback: this.loadVideo.bind(this),
        threshold: 400,
      });
      return;
    }

    this.#observeFallback();
  }

  #observeFallback() {
    if (!('IntersectionObserver' in window)) {
      this.loadVideo();
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.loadVideo();
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    observer.observe(this);
  }

  loadVideo() {
    if (this._hasLoadedVideo) return;
    this._hasLoadedVideo = true;

    const video = this.querySelector('video');
    if (!video) return;

    const sources = this.querySelectorAll('[data-video]');
    for (let i = 0; i < sources.length; i++) {
      const source = sources[i];
      const url = source.dataset.videoSource;
      if (url) source.setAttribute('src', url);
    }

    this.setAttribute('data-loaded', '');
    video.load();

    const hidePoster = () => {
      this.classList.add('is-playing');
    };

    video.addEventListener('playing', hidePoster, { once: true });
    video.addEventListener('loadeddata', hidePoster, { once: true });

    if (this.hasAttribute('data-autoplay')) {
      video.setAttribute('autoplay', '');
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  }
}

if (!customElements.get('deferred-autoplay-video')) {
  customElements.define('deferred-autoplay-video', DeferredAutoplayVideo);
}
